import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { Dayjs } from 'dayjs';
import type { TotalRevenueItem } from 'lumen/models';

export default class TotalRevenueReferenceItem extends Model {
  [Type] = 'total-revenue/reference-item' as const;

  @belongsTo('total-revenue/item', { async: false, inverse: 'referenceValues' })
  declare totalRevenueItem: TotalRevenueItem;

  @attr('number') declare nonRoomPremium: number;
  @attr('hoteldate') declare referenceFromDate: Dayjs;
  @attr declare referenceType: string;
  @attr('number') declare totalNonRoomRevenue: number;
  @attr('number') declare totalRevenue: number;
  @attr('number') declare totalRoomRevenue: number;

  @service('total-revenue')
  declare totalRevenueService: Services['total-revenue'];

  @tracked capacity?: number;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNonRoomPremiumChange() {
    return this.totalRevenueService.formatChange(this.nonRoomPremiumChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNonRoomPremiumRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.nonRoomPremiumRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenueChange() {
    return this.totalRevenueService.formatChange(
      this.totalNonRoomRevenueChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalNonRoomRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenueChange() {
    return this.totalRevenueService.formatChange(this.totalRevenueChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenueChange() {
    return this.totalRevenueService.formatChange(this.totalRoomRevenueChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRoomRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonRoomPremiumChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.nonRoomPremium,
      this.nonRoomPremium,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonRoomPremiumRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.nonRoomPremium,
      this.nonRoomPremium,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalNonRoomRevenueOrPAR,
      this.totalNonRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalNonRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalNonRoomRevenueOrPAR,
      this.totalNonRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalRevenueOrPAR,
      this.totalRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalRevenueOrPAR,
      this.totalRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalRoomRevenueOrPAR,
      this.totalRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalRoomRevenueOrPAR,
      this.totalRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translatedReferenceType() {
    return this.totalRevenueService.translateReferenceType(this.referenceType);
  }
}
